/* FORGE Klaviyo "Added to Cart" tracking.

   WHY THIS EXISTS. Klaviyo's Shopify integration does not deliver an Added-to-Cart event
   — its native metrics come from webhooks (Placed Order, Checkout Started) and onsite.js
   only contributes Active on Site and Viewed Product. Add-to-cart has to be pushed by the
   theme. The Pipeline theme did that from assets/custom.js by listening to Pipeline's
   `theme:cart:change` event.

   That did not survive the Forge rebuild. Forge has no custom.js and fires no
   `theme:cart:change`, so from the moment Forge went live the custom Added to Cart metric
   (SU8j2P) went to zero — verified against the Klaviyo API 2026-08-17: zero events every
   day 08-13 → 08-17 while Placed Order held at 7-15/day and theme-fired Viewed Product
   kept firing. The live flow RKAtKA "Abandoned Cart - Early (Custom Added to Cart)"
   triggers on that metric and had been dark the whole time. This file restores it.

   WHY NOT HOOK THE BUTTONS. Forge adds to cart through five different paths:
     1. assets/product-form.js       — Dawn's <product-form>, the main PDP + drawer upsell
     2. assets/forge-set-add.js      — the set module, one /cart/add.js with an items array
     3. assets/forge-cart-page-upsell.js — the cart-page upsell
     4. snippets/forge-cart-page-card.liquid — a REAL <form method="post">, no JS
     5. snippets/forge-set-item.liquid       — same, the no-JS fallback
   Wiring a callback into each is exactly the kind of per-path patch that rots the next
   time someone adds a sixth. So this listens at the two places every path must pass
   through instead: the network call, and the cart itself.

   HOW IT WORKS. We keep a snapshot of {variant_id: quantity} in sessionStorage and fire an
   event for any line whose quantity went UP. The snapshot is reconciled:
     - after any successful fetch() to /cart/add  — covers paths 1-3
     - on every page load                         — covers paths 4-5, which navigate the
       browser to /cart, so the add is only observable on the next page
   That page-load reconcile makes this strictly better than the Pipeline original, which
   listened to an AJAX-only event and therefore never saw a plain form post at all.

   The first time we ever see a cart we SEED the snapshot without firing — otherwise a
   shopper who already had three items would generate three phantom adds on first load.

   PAYLOAD SHAPE IS DELIBERATELY IDENTICAL to the Pipeline implementation, down to
   AddedItemQuantity being hardcoded to 1 and ItemPrice/RowTotal being strings while
   $value/AddedItemPrice are floats. It is not a typo. Metric SU8j2P has ~15 months of
   history in that shape and a live flow reads it; matching it means this reconnects to the
   existing metric rather than silently creating a second one. Fix the shape only
   deliberately, and expect to re-point the flow if you do. */
(function () {
  'use strict';

  var SNAPSHOT_KEY = 'forge:klaviyo:cart-snapshot';

  var cfgEl = document.getElementById('forge-klaviyo-config');
  if (!cfgEl) { return; }
  var cfg;
  try { cfg = JSON.parse(cfgEl.textContent); } catch (e) { return; }
  if (!cfg.account) { return; }

  /* Canonical storefront origin, so ProductURL/CheckoutURL match what Pipeline sent even
     if the shopper is on the myshopify domain. Liquid supplies it; origin is the fallback. */
  var ORIGIN = (cfg.origin || window.location.origin || '').replace(/\/$/, '');

  /* Klaviyo's documented queue pattern: before onsite.js loads this is a plain array and
     push() queues; after it loads push() delivers. Either way push() exists, so an event
     fired early is queued rather than dropped. */
  window.klaviyo = window.klaviyo || [];

  function itemsToMap(items) {
    var map = {};
    (items || []).forEach(function (item) {
      map[item.variant_id] = (map[item.variant_id] || 0) + item.quantity;
    });
    return map;
  }

  function readSnapshot() {
    try {
      var raw = sessionStorage.getItem(SNAPSHOT_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) { return null; }
  }

  function writeSnapshot(map) {
    try { sessionStorage.setItem(SNAPSHOT_KEY, JSON.stringify(map)); } catch (e) { /* private mode */ }
  }

  function absolute(url) {
    if (!url) { return ''; }
    if (url.indexOf('//') === 0) { return 'https:' + url; }
    if (url.charAt(0) === '/') { return ORIGIN + url; }
    return url;
  }

  function money(cents) {
    return parseFloat((cents / 100).toFixed(2));
  }

  function pushAddedToCart(item, cart) {
    var kl = window.klaviyo;
    if (!kl || typeof kl.push !== 'function') { return; }

    var itemNames = (cart.items || []).map(function (i) { return i.product_title; });
    var cartItems = (cart.items || []).map(function (i) {
      return {
        ProductID: i.product_id,
        SKU: i.sku,
        ProductName: i.product_title,
        Quantity: i.quantity,
        ItemPrice: money(i.price).toFixed(2),
        RowTotal: money(i.line_price).toFixed(2),
        ProductURL: absolute(i.url),
        ImageURL: absolute(i.image),
        ProductCategories: i.product_type ? [i.product_type] : []
      };
    });

    kl.push(['track', 'Added to Cart', {
      '$value': money(item.price),
      AddedItemProductName: item.product_title,
      AddedItemProductID: item.product_id,
      AddedItemSKU: item.sku,
      AddedItemCategories: item.product_type ? [item.product_type] : [],
      AddedItemImageURL: absolute(item.image),
      AddedItemURL: absolute(item.url),
      AddedItemPrice: money(item.price),
      AddedItemQuantity: 1,
      ItemNames: itemNames,
      CheckoutURL: ORIGIN + '/checkout',
      Items: cartItems
    }]);
  }

  function reconcile() {
    return fetch('/cart.js', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var next = itemsToMap(cart.items);
        var prev = readSnapshot();
        /* First sight of this session: establish the baseline, fire nothing. */
        if (prev === null) { writeSnapshot(next); return; }
        (cart.items || []).forEach(function (item) {
          if ((next[item.variant_id] || 0) > (prev[item.variant_id] || 0)) {
            pushAddedToCart(item, cart);
          }
        });
        writeSnapshot(next);
      })
      .catch(function () { /* never let tracking break the cart */ });
  }

  /* Serialise reconciles. Two adds in flight at once would otherwise both read the same
     stale snapshot and double-fire the second item. */
  var chain = Promise.resolve();
  function schedule() {
    chain = chain.then(reconcile, reconcile);
    return chain;
  }

  /* Covers the plain <form method="post"> paths, which land here after navigating. */
  schedule();

  /* Covers every JS path, present and future, without naming any of them. We observe the
     response and never alter it — the caller gets the original promise untouched. */
  var nativeFetch = window.fetch;
  if (typeof nativeFetch === 'function') {
    window.fetch = function (input, init) {
      var url = '';
      try {
        url = typeof input === 'string' ? input : (input && input.url) || '';
      } catch (e) { url = ''; }
      var isCartAdd = url.indexOf('/cart/add') !== -1;
      var promise = nativeFetch.apply(this, arguments);
      if (isCartAdd) {
        promise.then(function (res) {
          if (res && res.ok) { schedule(); }
        }, function () { /* the add itself failed; nothing to report */ });
      }
      return promise;
    };
  }
})();
